import React, { useState, useEffect } from 'react';
import { KanbanProvider, useKanban } from './context/KanbanContext';
import { ROL_LABELS, ModuloSistema } from './types';
import { VincularTelegramModal } from './components/VincularTelegramModal';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider, useSocket } from './context/SocketContext';
import { NotificationProvider } from './context/NotificationContext';
import { TimeTrackingProvider } from './context/TimeTrackingContext';
import { Toaster } from 'sileo';
import 'sileo/styles.css';
import { Header } from './components/Header';
import { LoginPage } from './components/LoginPage';
import { NotificationBell } from './components/NotificationBell';
import { VistaTablero } from './components/VistaTablero';
import { VistaAudios } from './components/VistaAudios';
import { VistaVentas } from './components/VistaVentas';

type TabKey = 'tablero' | 'audios' | 'ventas';

// Antes había 8 pestañas separadas del módulo 'kanban' acá — en el celular
// obligaba a scrollear toda la barra para encontrar cualquiera. Se agrupan
// en VistaTablero.tsx bajo un solo tab, con su propia sub-navegación
// adentro (mismo criterio que ya se usaba en Ventas). Audios y Ventas
// siguen aparte porque son de otro módulo de permisos.
const TABS: Array<{ key: TabKey; label: string; icon: string; modulo: ModuloSistema }> = [
    { key: 'tablero', label: 'Tablero', icon: '📋', modulo: 'kanban' },
    { key: 'audios', label: 'Audios', icon: '🎙️', modulo: 'audios' },
    { key: 'ventas', label: 'Ventas', icon: '📞', modulo: 'ventas' },
];

const WSListener: React.FC = () => {
    const { on, off } = useSocket();
    const { fetchKanbanBoard } = useKanban();
    useEffect(() => {
        const refresh = () => fetchKanbanBoard();
        on('tarea:created', refresh);
        on('tarea:updated', refresh);
        on('tarea:moved', refresh);
        on('tarea:deleted', refresh);
        on('cliente:updated', refresh);
        return () => {
            off('tarea:created', refresh);
            off('tarea:updated', refresh);
            off('tarea:moved', refresh);
            off('tarea:deleted', refresh);
            off('cliente:updated', refresh);
        };
    }, [on, off, fetchKanbanBoard]);
    return null;
};

const Dashboard: React.FC = () => {
    const { loading, fetchKanbanBoard } = useKanban();
    const { usuario, logout } = useAuth();
    const { connected } = useSocket();
    const [activeTab, setActiveTab] = useState<TabKey>('tablero');
    const [mostrarVincularTelegram, setMostrarVincularTelegram] = useState(false);

    // Gerente siempre ve todo, sin depender de la lista — mismo criterio
    // que requirePermiso en el backend (evita que un Gerente se bloquee un
    // módulo a sí mismo sin querer al editar sus propios permisos).
    const tabsVisibles = TABS.filter(t => usuario?.rol === 'gerente' || (usuario?.permisos_modulos ?? []).includes(t.modulo));

    useEffect(() => {
        if (tabsVisibles.length > 0 && !tabsVisibles.some(t => t.key === activeTab)) {
            setActiveTab(tabsVisibles[0].key);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabsVisibles.map(t => t.key).join(',')]);

    return (
        <div className="min-h-screen bg-slate-50">
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${connected ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                            {connected ? '🟢 WS conectado' : '⚪ WS'}
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <NotificationBell />
                        {usuario && (
                            <div className="flex items-center gap-2 pl-2 pr-1 py-1 bg-slate-100 rounded-lg">
                                <div className="w-7 h-7 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                    {usuario.nombre.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || '?'}
                                </div>
                                <div className="text-xs">
                                    <div className="font-medium text-slate-700">{usuario.nombre}</div>
                                    <div className="text-slate-500">{ROL_LABELS[usuario.rol]}</div>
                                </div>
                                <div className="flex items-center gap-0.5 ml-1 pl-1 border-l border-slate-300">
                                    <button
                                        onClick={() => setMostrarVincularTelegram(true)}
                                        className="w-8 h-8 flex items-center justify-center text-base text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                        title="Vincular Telegram"
                                    >
                                        🔗
                                    </button>
                                    <button
                                        onClick={logout}
                                        className="w-8 h-8 flex items-center justify-center text-base text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                        title="Cerrar sesión"
                                    >
                                        ⏻
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </header>
            {mostrarVincularTelegram && (
                <VincularTelegramModal onClose={() => setMostrarVincularTelegram(false)} />
            )}
            <Header onRefresh={fetchKanbanBoard} loading={loading} />
            <nav className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
                <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex gap-1 overflow-x-auto scrollbar-thin">
                        {tabsVisibles.map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                                    activeTab === tab.key
                                        ? 'border-slate-800 text-slate-900'
                                        : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
                                }`}
                            >
                                <span className="mr-1">{tab.icon}</span>{tab.label}
                            </button>
                        ))}
                    </div>
                </div>
            </nav>
            <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {activeTab === 'tablero' && <VistaTablero />}
                {activeTab === 'audios' && <VistaAudios />}
                {activeTab === 'ventas' && <VistaVentas />}
            </main>
        </div>
    );
};

const AppContent: React.FC = () => {
    const { token } = useAuth();
    if (!token) {
        return <LoginPage />;
    }
    return (
        <KanbanProvider>
            <SocketProvider>
                <NotificationProvider>
                    <TimeTrackingProvider>
                        <WSListener />
                        <Dashboard />
                        <Toaster position="bottom-right" />
                    </TimeTrackingProvider>
                </NotificationProvider>
            </SocketProvider>
        </KanbanProvider>
    );
};

const App: React.FC = () => {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
};

export default App;
